import { functietitelLijst, talentpoolLijst, type Referentielijst } from '../../api/referentie.ts';
import type { KandidaatFilter } from '../../api/typen.ts';
import { datum } from '../../helpers/formatteer.ts';
import { kandidaatstatusLabels, type Optie, opties, rijbewijsLabels } from '../../helpers/labels.ts';

export type Sorteerveld = NonNullable<KandidaatFilter['sorteer']>;
export type Sorteerrichting = NonNullable<KandidaatFilter['richting']>;
export type OptioneelVeld = NonNullable<KandidaatFilter['gevuld']>[number];

type Tekstparameter =
  'voornaam' | 'achternaam' | 'email' | 'telefoon' | 'linkedinUrl' | 'straatHuisnummer' | 'woonplaats' | 'bron';

/** Een datumfilter gebruikt de parameters `<naam>Van` en `<naam>Tot`. */
type Datumparameter = 'geboortedatum' | 'aangemaakt' | 'gewijzigd';

export type Eigenschapfilter =
  | { soort: 'tekst'; parameter: Tekstparameter }
  | { soort: 'keuze'; parameter: 'status' | 'rijbewijs'; opties: Optie<string>[] }
  | {
      soort: 'referentie';
      parameter: 'talentpoolId' | 'functietitelId';
      lijst: Referentielijst<{ id: string; naam: string }, unknown>;
    }
  | { soort: 'datum'; parameter: Datumparameter };

/** Een eigenschap van een kandidaat: een kolom in de lijst waarop te sorteren en meestal te filteren is. */
export interface Eigenschap {
  sleutel: Sorteerveld;
  label: string;
  filter?: Eigenschapfilter;
  /** Het veld waarmee op leeg of ingevuld te filteren is, als de eigenschap leeg kan zijn. */
  optioneel?: OptioneelVeld;
}

const tekst = (parameter: Tekstparameter): Eigenschapfilter => ({ soort: 'tekst', parameter });

export const kandidaatEigenschappen: readonly Eigenschap[] = [
  { sleutel: 'naam', label: 'Naam' },
  { sleutel: 'voornaam', label: 'Voornaam', filter: tekst('voornaam') },
  { sleutel: 'achternaam', label: 'Achternaam', filter: tekst('achternaam') },
  {
    sleutel: 'status',
    label: 'Status',
    filter: { soort: 'keuze', parameter: 'status', opties: opties(kandidaatstatusLabels) },
  },
  { sleutel: 'email', label: 'E-mail', filter: tekst('email'), optioneel: 'email' },
  { sleutel: 'telefoon', label: 'Telefoon', filter: tekst('telefoon'), optioneel: 'telefoon' },
  { sleutel: 'linkedinUrl', label: 'LinkedIn', filter: tekst('linkedinUrl'), optioneel: 'linkedinUrl' },
  {
    sleutel: 'geboortedatum',
    label: 'Geboortedatum',
    filter: { soort: 'datum', parameter: 'geboortedatum' },
    optioneel: 'geboortedatum',
  },
  { sleutel: 'straatHuisnummer', label: 'Adres', filter: tekst('straatHuisnummer'), optioneel: 'straatHuisnummer' },
  { sleutel: 'woonplaats', label: 'Woonplaats', filter: tekst('woonplaats'), optioneel: 'woonplaats' },
  {
    sleutel: 'rijbewijzen',
    label: 'Rijbewijzen',
    filter: { soort: 'keuze', parameter: 'rijbewijs', opties: opties(rijbewijsLabels) },
    optioneel: 'rijbewijzen',
  },
  { sleutel: 'bron', label: 'Bron', filter: tekst('bron'), optioneel: 'bron' },
  {
    sleutel: 'talentpools',
    label: 'Talentpools',
    filter: { soort: 'referentie', parameter: 'talentpoolId', lijst: talentpoolLijst },
    optioneel: 'talentpools',
  },
  {
    sleutel: 'functietitels',
    label: 'Functietitels',
    filter: { soort: 'referentie', parameter: 'functietitelId', lijst: functietitelLijst },
    optioneel: 'functietitels',
  },
  { sleutel: 'aangemaaktOp', label: 'Aangemaakt', filter: { soort: 'datum', parameter: 'aangemaakt' } },
  { sleutel: 'gewijzigdOp', label: 'Gewijzigd', filter: { soort: 'datum', parameter: 'gewijzigd' } },
];

/** De kolom die altijd zichtbaar is, omdat hij naar het profiel linkt. */
export const vasteKolom: Sorteerveld = 'naam';

export const standaardKolommen: readonly Sorteerveld[] = [
  'naam',
  'status',
  'email',
  'telefoon',
  'bron',
  'aangemaaktOp',
];

const sorteervelden = kandidaatEigenschappen.map((eigenschap) => eigenschap.sleutel);
const optioneleVelden = kandidaatEigenschappen.flatMap((eigenschap) =>
  eigenschap.optioneel ? [eigenschap.optioneel] : [],
);

/** Leest een waarde uit het adres; een ontbrekende waarde is een lege tekst. */
export type Lees = (naam: string) => string;

export type Filtermodus = 'waarde' | 'leeg' | 'gevuld';

/** De stand van het filter op één eigenschap, zoals de gebruiker hem invult. */
export interface Filterstand {
  modus: Filtermodus;
  tekst: string;
  keuzes: string[];
  van: string;
  tot: string;
}

export const legeFilterstand: Filterstand = { modus: 'waarde', tekst: '', keuzes: [], van: '', tot: '' };

/**
 * Splitst een lijst uit het adres, zoals `actief,nieuw`.
 * @param waarde De waarden gescheiden door komma's.
 * @returns De waarden, zonder lege.
 */
export function splits(waarde: string): string[] {
  return waarde
    .split(',')
    .map((deel) => deel.trim())
    .filter(Boolean);
}

function bekend<T extends string>(waarden: readonly string[], toegestaan: readonly T[]): T[] {
  return waarden.filter((waarde): waarde is T => (toegestaan as readonly string[]).includes(waarde));
}

const uuidPatroon = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const datumPatroon = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Leest het filter op een eigenschap uit het adres.
 * @param eigenschap De eigenschap.
 * @param krijg Leest een parameter uit het adres.
 * @returns De stand van het filter, of `null` als er niet op de eigenschap gefilterd wordt.
 */
export function leesFilter(eigenschap: Eigenschap, krijg: Lees): Filterstand | null {
  const { filter, optioneel } = eigenschap;
  if (optioneel && splits(krijg('leeg')).includes(optioneel)) {
    return { ...legeFilterstand, modus: 'leeg' };
  }
  if (optioneel && splits(krijg('gevuld')).includes(optioneel)) {
    return { ...legeFilterstand, modus: 'gevuld' };
  }
  switch (filter?.soort) {
    case 'tekst': {
      const waarde = krijg(filter.parameter).trim();
      return waarde ? { ...legeFilterstand, tekst: waarde } : null;
    }
    case 'keuze':
    case 'referentie': {
      const keuzes = splits(krijg(filter.parameter));
      return keuzes.length > 0 ? { ...legeFilterstand, keuzes } : null;
    }
    case 'datum': {
      const van = krijg(`${filter.parameter}Van`);
      const tot = krijg(`${filter.parameter}Tot`);
      return van || tot ? { ...legeFilterstand, van, tot } : null;
    }
    default:
      return null;
  }
}

/**
 * Bepaalt de wijzigingen in het adres om het filter op een eigenschap te zetten of te wissen.
 * @param eigenschap De eigenschap.
 * @param stand De nieuwe stand, of `null` om het filter te wissen. Een stand zonder waarde wist het filter ook.
 * @param krijg Leest een parameter uit het huidige adres.
 * @returns De parameters met hun nieuwe waarde; `undefined` haalt een parameter weg.
 */
export function schrijfFilter(
  eigenschap: Eigenschap,
  stand: Filterstand | null,
  krijg: Lees,
): Record<string, string | undefined> {
  const { filter, optioneel } = eigenschap;
  const modus = stand?.modus ?? 'waarde';
  const waarde = stand && modus === 'waarde' ? stand : legeFilterstand;
  const wijzigingen: Record<string, string | undefined> = {};
  if (optioneel) {
    const leeg = splits(krijg('leeg')).filter((veld) => veld !== optioneel);
    const gevuld = splits(krijg('gevuld')).filter((veld) => veld !== optioneel);
    if (stand && modus === 'leeg') {
      leeg.push(optioneel);
    }
    if (stand && modus === 'gevuld') {
      gevuld.push(optioneel);
    }
    wijzigingen.leeg = leeg.join(',') || undefined;
    wijzigingen.gevuld = gevuld.join(',') || undefined;
  }
  switch (filter?.soort) {
    case 'tekst':
      wijzigingen[filter.parameter] = waarde.tekst.trim() || undefined;
      break;
    case 'keuze':
    case 'referentie':
      wijzigingen[filter.parameter] = waarde.keuzes.join(',') || undefined;
      break;
    case 'datum':
      wijzigingen[`${filter.parameter}Van`] = waarde.van || undefined;
      wijzigingen[`${filter.parameter}Tot`] = waarde.tot || undefined;
      break;
    default:
      break;
  }
  return wijzigingen;
}

/**
 * De wijzigingen in het adres om alle filters op eigenschappen te wissen. De zoekterm blijft staan.
 * @returns Alle filterparameters met de waarde `undefined`.
 */
export function wisAlleFilters(): Record<string, undefined> {
  const leeg = () => '';
  return Object.fromEntries(
    kandidaatEigenschappen.flatMap((eigenschap) =>
      Object.keys(schrijfFilter(eigenschap, null, leeg)).map((naam) => [naam, undefined]),
    ),
  );
}

/**
 * Zet de filters en de sortering uit het adres om naar de queryparameters van de API. Onbekende waarden, zoals
 * een verouderde link, vallen weg in plaats van een fout te geven.
 * @param krijg Leest een parameter uit het adres.
 * @returns De filters en de sortering, zonder zoekterm en paginering.
 */
export function naarApiFilter(krijg: Lees): KandidaatFilter {
  const filter: Record<string, string | string[]> = {};
  for (const { filter: soort } of kandidaatEigenschappen) {
    switch (soort?.soort) {
      case 'tekst': {
        const waarde = krijg(soort.parameter).trim();
        if (waarde) {
          filter[soort.parameter] = waarde;
        }
        break;
      }
      case 'keuze':
      case 'referentie': {
        const waarden = splits(krijg(soort.parameter));
        const geldig =
          soort.soort === 'keuze'
            ? bekend(
                waarden,
                soort.opties.map((optie) => optie.waarde),
              )
            : waarden.filter((waarde) => uuidPatroon.test(waarde));
        if (geldig.length > 0) {
          filter[soort.parameter] = geldig;
        }
        break;
      }
      case 'datum':
        for (const naam of [`${soort.parameter}Van`, `${soort.parameter}Tot`]) {
          const waarde = krijg(naam);
          if (datumPatroon.test(waarde)) {
            filter[naam] = waarde;
          }
        }
        break;
      default:
        break;
    }
  }
  for (const naam of ['leeg', 'gevuld'] as const) {
    const velden = bekend(splits(krijg(naam)), optioneleVelden);
    if (velden.length > 0) {
      filter[naam] = velden;
    }
  }
  const { veld, richting } = leesSortering(krijg);
  if (veld !== 'naam') {
    filter.sorteer = veld;
  }
  if (richting === 'aflopend') {
    filter.richting = richting;
  }
  return filter as KandidaatFilter;
}

/**
 * Leest de sortering uit het adres.
 * @param krijg Leest een parameter uit het adres.
 * @returns Het veld en de richting; standaard oplopend op naam.
 */
export function leesSortering(krijg: Lees): { veld: Sorteerveld; richting: Sorteerrichting } {
  const [veld = 'naam'] = bekend([krijg('sorteer')], sorteervelden);
  return { veld, richting: krijg('richting') === 'aflopend' ? 'aflopend' : 'oplopend' };
}

/**
 * Maakt van een bewaarde sortering een geldige: een bekend veld en een richting.
 * @param bewaard Wat er bewaard was, van onbekende vorm.
 * @returns De sortering, of de standaardsortering (oplopend op naam) als er niets bruikbaars bewaard was.
 */
export function geldigeSortering(bewaard: unknown): { veld: Sorteerveld; richting: Sorteerrichting } {
  const waarde = typeof bewaard === 'object' && bewaard !== null ? (bewaard as Record<string, unknown>) : {};
  return leesSortering((naam) => {
    const veld = waarde[naam === 'sorteer' ? 'veld' : naam];
    return typeof veld === 'string' ? veld : '';
  });
}

/**
 * Of het adres een sortering bevat. Zonder sortering in het adres geldt de bewaarde sortering.
 * @param krijg Leest een parameter uit het adres.
 * @returns `true` als `sorteer` of `richting` in het adres staat.
 */
export function heeftSortering(krijg: Lees): boolean {
  return krijg('sorteer') !== '' || krijg('richting') !== '';
}

/**
 * Zet een sortering om naar de parameters van het adres.
 * @param sortering Het veld en de richting.
 * @returns De parameters `sorteer` en `richting`; de standaardsortering staat er niet in.
 */
export function sorteerparameters(sortering: {
  veld: Sorteerveld;
  richting: Sorteerrichting;
}): Record<string, string | undefined> {
  return {
    sorteer: sortering.veld === 'naam' ? undefined : sortering.veld,
    richting: sortering.richting === 'aflopend' ? 'aflopend' : undefined,
  };
}

/**
 * Bepaalt de sortering na een klik op een kolomkop: eerst oplopend, bij een tweede klik aflopend.
 * @param huidig De huidige sortering.
 * @param veld De kolom waarop geklikt is.
 * @returns De parameters `sorteer` en `richting` voor het adres.
 */
export function volgendeSortering(
  huidig: { veld: Sorteerveld; richting: Sorteerrichting },
  veld: Sorteerveld,
): Record<string, string | undefined> {
  const richting = huidig.veld === veld && huidig.richting === 'oplopend' ? 'aflopend' : 'oplopend';
  return sorteerparameters({ veld, richting });
}

/**
 * Vat een actief filter samen voor op een filterchip.
 * @param eigenschap De eigenschap.
 * @param stand De stand van het filter.
 * @param naamVan Geeft de naam bij een id uit een referentielijst.
 * @returns Bijvoorbeeld `bevat "jan"`, `Actief, Nieuw` of `vanaf 01-01-2026`.
 */
export function samenvatting(
  eigenschap: Eigenschap,
  stand: Filterstand,
  naamVan: (id: string) => string = (id) => id,
): string {
  if (stand.modus === 'leeg') {
    return 'is leeg';
  }
  if (stand.modus === 'gevuld') {
    return 'is ingevuld';
  }
  const filter = eigenschap.filter;
  switch (filter?.soort) {
    case 'tekst':
      return `bevat "${stand.tekst}"`;
    case 'keuze':
      return stand.keuzes
        .map((keuze) => filter.opties.find((optie) => optie.waarde === keuze)?.label ?? keuze)
        .join(', ');
    case 'referentie':
      return stand.keuzes.map(naamVan).join(', ');
    case 'datum':
      if (stand.van && stand.tot) {
        return `${datum(stand.van)} – ${datum(stand.tot)}`;
      }
      return stand.van ? `vanaf ${datum(stand.van)}` : `tot en met ${datum(stand.tot)}`;
    default:
      return '';
  }
}

/**
 * Maakt van een bewaarde kolomindeling een geldige lijst: alleen bekende kolommen, in de vaste volgorde, met
 * altijd de naam erbij.
 * @param bewaard Wat er bewaard was, van onbekende vorm.
 * @returns De zichtbare kolommen, of de standaardindeling als er niets bruikbaars bewaard was.
 */
export function geldigeKolommen(bewaard: unknown): Sorteerveld[] {
  if (!Array.isArray(bewaard)) {
    return [...standaardKolommen];
  }
  const gekozen = new Set([vasteKolom, ...bekend(bewaard.map(String), sorteervelden)]);
  return sorteervelden.filter((veld) => gekozen.has(veld));
}
